import React, { useState, useEffect } from "react";
import { Card, Spin, Carousel, Grid, Row, Col } from "antd";
import { useNavigate } from "react-router-dom";
import { getBlogs } from "@/services/common/blogs";
import { getCountryNameByIsoCode } from "@/helpers/countryList";
import slugify from "slugify";
import Container from "@/components/container/Container";
import ButtonComponent from "@/components/button/Button";
import styles from "@/components/homepage/CountryBlogsSection.module.scss";

const { useBreakpoint } = Grid;

export default function CountryBlogList() {
  const [countryBlogs, setCountryBlogs] = useState(null);
  const navigate = useNavigate();
  const screens = useBreakpoint();

  useEffect(() => {
    async function fetchCountryBlogs() {
      try {
        const all = await getBlogs();
        setCountryBlogs(all.filter((b) => b.metadata?.countryCode));
      } catch (err) {
        console.error("Error loading country blogs:", err);
        setCountryBlogs([]);
      }
    }
    fetchCountryBlogs();
  }, []);

  if (countryBlogs === null) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  const renderCard = (blog) => {
    const country = getCountryNameByIsoCode(blog.metadata.countryCode);
    const author = blog?.authorName || "Author";
    const authorSlug = slugify(author, {
      lower: true,
      strict: true,
      replacement: "-",
      remove: /[^\w\s-]/g,
    });
    const description =
      (blog.contents[0]?.sectionContent || "").replace(/<[^>]+>/g, "").slice(0, 150) + "...";

    return (
      <Card
        key={blog.id}
        onClick={() => navigate(`/blog/${authorSlug}/${blog.slug}/${blog.id}`)}
        hoverable
        className={styles.card}
        styles={{
          body: {
            padding: "var(--ant-card-padding-mobile, 1rem)",
          },
        }}
        cover={
          <div className={styles.coverCarouselWrapper}>
            <Carousel
              dots={blog.coverImages.length > 1}
              autoplay
              autoplaySpeed={3000}
              className={styles.innerCarousel}
            >
              {(blog.coverImages.length
                ? blog.coverImages
                : [
                    `https://flagcdn.com/w640/${blog.metadata.countryCode
                      .slice(0, -1)
                      .toLowerCase()}.png`,
                  ]
              ).map((src, idx) => (
                <div key={idx} className={styles.coverWrapper}>
                  <img src={src} alt={country} className={styles.coverImage} />
                </div>
              ))}
            </Carousel>
          </div>
        }
      >
        <h3 className={styles.countryName}>{country}</h3>
        <p className={styles.description}>{description}</p>
        <ButtonComponent
          onClick={() => navigate(`/blog/${authorSlug}/${blog.slug}/${blog.id}`)}
          block
          className={styles.cta}
        >
          Know More
        </ButtonComponent>
      </Card>
    );
  };

  return (
    <Container className={styles.container}>
      <section className={styles.countryBlogsSection}>
        <h2 className={styles.title}>Recommended countries for study:</h2>

        {screens.md ? (
          // Desktop/Tablet: Grid
          <Row gutter={[24, 24]}>
            {countryBlogs.map((blog) => (
              <Col key={blog.id} xs={24} sm={12} md={12} lg={8} xl={6} xxl={6}>
                {renderCard(blog)}
              </Col>
            ))}
          </Row>
        ) : (
          // Mobile: Carousel
          <Carousel
            dots
            infinite
            speed={500}
            slidesToShow={2}
            slidesToScroll={1}
            draggable
            swipeToSlide
            className={styles.outerCarousel}
          >
            {countryBlogs.map((blog) => (
              <div key={blog.id} className={styles.slideWrapper}>
                {renderCard(blog)}
              </div>
            ))}
          </Carousel>
        )}
      </section>
    </Container>
  );
}
